import {
  TransferType,
  useAllCategoriesQuery,
  useAllCategoryGroupsQuery,
  useAllPayeesQuery,
} from '@transactions/graphql';
import {
  ArrowLeftRight,
  CircleDollarSign,
  CornerDownLeft,
  CreditCard,
  HandCoins,
  RefreshCw,
  Ticket,
  Undo2,
  X,
} from 'lucide-react';
import * as React from 'react';
import {CategoryAvatar} from 'src/CategoryAvatar';
import {
  PayeeFace,
  ScreenFrame,
  ScreenList,
  ScreenRow,
  ScreenSection,
} from 'src/CreateSheet/parts';
import {TextInput} from 'src/inputs/TextInput';
import {TRANSFER_TYPE_LABELS} from 'src/TransferKindSelect';
import type {LucideIcon} from 'lucide-react';

type Suggested = {name: string; detail: string};

// A screen lists at most this many rows before search has to narrow it.
const BROWSE_LIMIT = 60;

const matches = (name: string, query: string) =>
  name.toLowerCase().includes(query.trim().toLowerCase());

/** Payee: this merchant's history first, then every payee, searchable. */
export const PayeeScreen = ({
  title,
  value,
  suggested,
  logos,
  onPick,
  onClear,
  onBack,
}: {
  title: string;
  value: string;
  suggested: Suggested[];
  logos: Map<string, string>;
  onPick: (payee: string) => void;
  /** Offered when the payee is optional and one is set. */
  onClear?: () => void;
  onBack: () => void;
}) => {
  const [query, setQuery] = React.useState('');
  const {data} = useAllPayeesQuery();
  const all = data?.allPayees ?? [];
  const trimmed = query.trim();
  const hits = all.filter((p) => matches(p, query)).slice(0, BROWSE_LIMIT);
  const isNew = trimmed !== '' && !all.some((p) => p.toLowerCase() === trimmed.toLowerCase());
  const row = (name: string, detail?: string) => (
    <ScreenRow
      key={name}
      onClick={() => onPick(name)}
      leading={<PayeeFace name={name} logo={logos.get(name)} className="size-7 rounded-lg text-xs" />}
      label={name}
      detail={detail}
      selected={name === value}
      suggested={detail != null}
    />
  );
  return (
    <ScreenFrame title={title} onBack={onBack}>
      <TextInput autoFocus placeholder="Search payees" value={query} onChange={setQuery} />
      {onClear != null && trimmed === '' && (
        <ScreenList>
          <ScreenRow
            onClick={onClear}
            leading={<X className="text-muted-foreground size-4" />}
            label="No payee"
          />
        </ScreenList>
      )}
      {isNew && (
        <ScreenList>
          <ScreenRow
            onClick={() => onPick(trimmed)}
            leading={<CornerDownLeft className="text-muted-foreground size-4" />}
            label={`New payee “${trimmed}”`}
          />
        </ScreenList>
      )}
      {trimmed === '' && suggested.length > 0 && (
        <>
          <ScreenSection>This merchant</ScreenSection>
          <ScreenList>{suggested.map((s) => row(s.name, s.detail))}</ScreenList>
        </>
      )}
      {hits.length > 0 && (
        <>
          <ScreenSection>{trimmed === '' ? 'All payees' : 'Matching'}</ScreenSection>
          <ScreenList>{hits.map((p) => row(p))}</ScreenList>
        </>
      )}
    </ScreenFrame>
  );
};

/**
 * Category: this merchant's history first, then your categories in their
 * groups (or alphabetically), searchable, with a way to add a new one.
 */
export const CategoryScreen = ({
  value,
  suggested,
  onPick,
  onBack,
}: {
  value: string | null;
  suggested: Suggested[];
  onPick: (category: string) => void;
  onBack: () => void;
}) => {
  const [query, setQuery] = React.useState('');
  const {data} = useAllCategoriesQuery();
  const {data: groupsData} = useAllCategoryGroupsQuery();
  const categories = data?.allCategories ?? [];
  const byName = new Map(categories.map((c) => [c.name, c]));
  const trimmed = query.trim();

  const sections = React.useMemo(() => {
    const groups = groupsData?.allCategoryGroups ?? [];
    const byId = new Map(categories.map((c) => [c._id, c]));
    const grouped = new Set(groups.flatMap((g) => g.categoryIds));
    const out = groups
      .map((g) => ({
        key: g._id,
        label: g.name,
        names: g.categoryIds.flatMap((id) => {
          const c = byId.get(id);
          return c ? [c.name] : [];
        }),
      }))
      .filter((s) => s.names.length > 0);
    const rest = categories
      .filter((c) => !grouped.has(c._id))
      .map((c) => c.name)
      .sort((a, b) => a.localeCompare(b));
    if (rest.length > 0) {
      out.push({key: '__rest__', label: out.length > 0 ? 'Ungrouped' : 'All categories', names: rest});
    }
    return out;
  }, [categories, groupsData]);

  const row = (name: string, detail?: string) => {
    const category = byName.get(name) ?? {name};
    return (
      <ScreenRow
        key={name}
        onClick={() => onPick(name)}
        leading={<CategoryAvatar category={category} className="size-7 rounded-lg max-md:size-7" />}
        label={name}
        detail={detail}
        selected={name === value}
        suggested={detail != null}
      />
    );
  };
  const hits = categories.filter((c) => matches(c.name, query)).map((c) => c.name);
  const isNew = trimmed !== '' && !categories.some((c) => c.name.toLowerCase() === trimmed.toLowerCase());

  return (
    <ScreenFrame title="Category" onBack={onBack}>
      <TextInput autoFocus placeholder={`Search ${categories.length} categories`} value={query} onChange={setQuery} />
      {trimmed !== '' ? (
        <>
          {isNew && (
            <ScreenList>
              <ScreenRow
                onClick={() => onPick(trimmed)}
                leading={<CornerDownLeft className="text-muted-foreground size-4" />}
                label={`Add “${trimmed}”`}
              />
            </ScreenList>
          )}
          {hits.length > 0 && <ScreenList>{hits.slice(0, BROWSE_LIMIT).map((n) => row(n))}</ScreenList>}
        </>
      ) : (
        <>
          {suggested.length > 0 && (
            <>
              <ScreenSection>This merchant</ScreenSection>
              <ScreenList>{suggested.map((s) => row(s.name, s.detail))}</ScreenList>
            </>
          )}
          {sections.map((s) => (
            <React.Fragment key={s.key}>
              <ScreenSection>{s.label}</ScreenSection>
              <ScreenList>{s.names.map((n) => row(n))}</ScreenList>
            </React.Fragment>
          ))}
        </>
      )}
    </ScreenFrame>
  );
};

export const TRANSFER_TYPE_ICONS: Record<TransferType, LucideIcon> = {
  [TransferType.CreditCardPayment]: CreditCard,
  [TransferType.AccountTransfer]: ArrowLeftRight,
  [TransferType.Prepaid]: Ticket,
  [TransferType.Refund]: Undo2,
  [TransferType.Reimbursement]: HandCoins,
  [TransferType.Conversion]: RefreshCw,
};

/** A transfer type's tile, the counterpart of a category's avatar. */
export const TransferTypeTile = ({type, className = 'size-6'}: {type: TransferType | null; className?: string}) => {
  const Icon = type != null ? TRANSFER_TYPE_ICONS[type] : CircleDollarSign;
  return (
    <span
      aria-hidden
      className={`flex shrink-0 items-center justify-center rounded-md bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 ${className}`}
    >
      <Icon className="size-[60%]" />
    </span>
  );
};

/** Transfer type: every type but conversion (which a selection decides). */
export const TransferTypeScreen = ({
  value,
  suggested,
  onPick,
  onBack,
}: {
  value: TransferType | null;
  suggested: TransferType | null;
  onPick: (type: TransferType) => void;
  onBack: () => void;
}) => (
  <ScreenFrame title="Transfer type" onBack={onBack}>
    <ScreenList>
      {Object.values(TransferType)
        .filter((t) => t !== TransferType.Conversion)
        .map((t) => (
          <ScreenRow
            key={t}
            onClick={() => onPick(t)}
            leading={<TransferTypeTile type={t} className="size-7 rounded-lg" />}
            label={TRANSFER_TYPE_LABELS[t]}
            selected={t === value}
            suggested={t === suggested}
          />
        ))}
    </ScreenList>
  </ScreenFrame>
);
